import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { FiatDepositConfig } from "@modules/+new/payments/features/fiat-deposit/domain/config/fiat-deposit-config";
import { FiatDepositStepsParams } from "@modules/+new/payments/features/fiat-deposit/domain/entities/fiat-deposit-steps-params.model";
import { FiatDepositParamNames } from "@modules/+new/payments/features/fiat-deposit/domain/entities/fiat-deposit-param-names.enum";
import { FiatDepositStepsData } from "@modules/+new/payments/features/fiat-deposit/domain/entities/fiat-deposit-steps-data.model";
import { switchMap, take } from "rxjs/operators";
import { FiatDepositStateService } from "@modules/+new/payments/features/fiat-deposit/domain/+state/fiat-deposit.state.service";
import { from, Observable } from "rxjs";
import { FiatDepositStepNames } from "@modules/+new/payments/features/fiat-deposit/domain/entities/fiat-deposit-step-names.enum";
import { GenericCollection } from "@modules/+new/payments/shared-payments/entities/generic-collection.model";

@Injectable({
	providedIn: "any"
})
export class FiatDepositNavigationService {
	public constructor(
		private router: Router,
		private readonly fiatDepositStateService: FiatDepositStateService
	) {}

	public navigateToStep$(stepName: FiatDepositStepNames, params?: FiatDepositStepsParams): Observable<boolean> {
		return this.fiatDepositStateService.getStepsData$().pipe(
			take(1),
			switchMap((stepsData: FiatDepositStepsData) => {
				const queryParams = this.prepareQueryParams(params, stepsData);
				return from(this.router.navigate([{ outlets: this.prepareOutlets(stepName) }], { queryParams }));
			})
		);
	}

	private prepareQueryParams(
		params: FiatDepositStepsParams,
		stepsData: FiatDepositStepsData
	): GenericCollection<string> {
		const paramsNames: FiatDepositParamNames[] = [
			FiatDepositParamNames.FiatPid,
			FiatDepositParamNames.Wallet,
			FiatDepositParamNames.PaymentOption,
			FiatDepositParamNames.DepositAmount,
			FiatDepositParamNames.IncludingFees,
			FiatDepositParamNames.BankProvider,
			FiatDepositParamNames.ParentBank,
			FiatDepositParamNames.Bank,
			FiatDepositParamNames.FromSummary,
			FiatDepositParamNames.FromBack,
			FiatDepositParamNames.Transaction,
			FiatDepositParamNames.PaymentUrl,
			FiatDepositParamNames.AssetSymbol,
			FiatDepositParamNames.NetworkId
		];

		return paramsNames.reduce((acc: GenericCollection<string>, paramName: FiatDepositParamNames) => {
			const value = this.prepareParamValue(paramName, params, stepsData);
			if (value !== null) {
				acc[paramName] = value + "";
			}
			return acc;
		}, {});
	}

	private prepareParamValue(
		paramName: FiatDepositParamNames,
		params: FiatDepositStepsParams,
		stepsData: FiatDepositStepsData
	): string | number | boolean | null {
		if (this.hasParamValue(params?.[paramName])) {
			return params?.[paramName];
		}
		if (this.hasParamValue(stepsData?.[paramName])) {
			return stepsData?.[paramName];
		}
		return null;
	}

	private hasParamValue(value: unknown): boolean {
		return !!value || value === 0 || typeof value === "boolean";
	}

	private prepareOutlets(stepName: FiatDepositStepNames): GenericCollection<string[]> {
		return { [FiatDepositConfig.outletName]: [FiatDepositConfig.entryPathName, stepName] };
	}
}
